import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { FadeUp } from "@/components/Reveal";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const categories = ["All", "Healing & Recovery", "Muscle Building", "Weight Loss", "Cellular", "Cognitive", "Accessories"];
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active !== "All") params.category = active;
    if (debounced) params.search = debounced;
    api
      .get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [active, debounced]);

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-40" data-testid="shop-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <FadeUp>
          <p className="label text-accent mb-6">/ The Collection</p>
          <h1 className="font-display text-4xl md:text-6xl font-light leading-[1.02] tracking-tight max-w-4xl italic">
            “Research is seeing what everybody else has seen and thinking what nobody else has thought.”
          </h1>
        </FadeUp>

        <div className="mt-12 max-w-xl">
          <div className="relative flex items-center border-b border-border focus-within:border-accent transition-colors">
            <Search size={18} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, benefit, or ingredient…"
              className="flex-1 bg-transparent py-3 px-3 text-sm focus:outline-none placeholder:text-muted-foreground"
              data-testid="shop-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="shop-search-clear" aria-label="Clear search">
                <X size={18} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`label px-5 py-2.5 border transition-colors duration-300 ${
                active === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-foreground/70 border-border hover:border-foreground"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-secondary animate-pulse border border-border" />
            ))}
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-testid="products-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <p className="mt-20 label text-muted-foreground" data-testid="no-products">
            {debounced ? `No products match “${debounced}”.` : "No products in this category yet."}
          </p>
        )}
      </div>
    </main>
  );
}
